import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Line } from 'react-chartjs-2'
import IconButton from '@material-ui/core/IconButton'
import { ChevronLeft, ChevronRight } from '@material-ui/icons'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import cn from 'classnames'
import * as COLOR from 'constants/colors'
import { setActiveDateRange } from 'reducers/sensor/reducer'
import CircleLoader from 'components/Loading/CircleLoader'
import {
  MULTIPLE_VALUE_TYPE,
  MULTIPLE_ROW_TYPE,
  SINGLE_VALUE_TYPE,
} from 'constants/index'

class ControlChart extends Component {
  getDataByIndex = index => {
    const {
      intl: { formatMessage },
      data: { items, type },
    } = this.props
    const dataPoint = items[index].data

    if (type === SINGLE_VALUE_TYPE && Array.isArray(items)) {
      let oneValueResult = dataPoint

      if (dataPoint === 0) {
        oneValueResult = `${dataPoint}`
        // eslint-disable-next-line no-restricted-globals
      } else if (isNaN(dataPoint) && dataPoint.includes('_')) {
        oneValueResult = formatMessage({ id: dataPoint })
      }

      return oneValueResult
    }

    if (type === MULTIPLE_VALUE_TYPE && Array.isArray(items)) {
      return dataPoint.map(item =>
        // eslint-disable-next-line no-restricted-globals
        isNaN(item) ? formatMessage({ id: item }) : item,
      )
    }

    if (type === MULTIPLE_ROW_TYPE && Array.isArray(items)) {
      // eslint-disable-next-line array-callback-return
      const multipleData = dataPoint.map(item => {
        for (const key of Object.keys(item)) {
          if (item.hasOwnProperty(key) && Array.isArray(item.data)) {
            const results = item.data
              .map(subItem => {
                // eslint-disable-next-line no-restricted-globals
                if (isNaN(subItem) && subItem.includes('_')) {
                  return formatMessage({ id: subItem })
                }

                return subItem
              })
              .join(', ')

            return `${item[key]}: ${results}`
          }
        }
      })

      return multipleData.length >= 5 ? multipleData.join(', ') : multipleData
    }

    return []
  }

  getFormattedData = () => {
    const {
      data: { items },
    } = this.props

    if (items && items.length) {
      return items.map(item => {
        const result = {}
        for (const key in item) {
          if (item.hasOwnProperty(key)) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            key !== 'date' ? (result.y = 5) : (result.x = item[key])
          }
        }

        return result
      })
    }

    return []
  }

  getData = () => {
    const { label } = this.props
    const chartData = this.getFormattedData()

    return {
      datasets: [
        {
          label,
          fill: false,
          lineTension: 0,
          pointRadius: 6,
          pointHitRadius: 6,
          pointHoverRadius: 6,
          backgroundColor: COLOR.DB_MINT,
          borderColor: COLOR.DB_MINT,
          showLine: false,
          data: chartData,
        },
      ],
    }
  }

  getOptions = () => {
    const { label, range, doubleChartsOn, typeLevel } = this.props
    const dateDiff = range.get('endDate').diff(range.get('startDate'), 'days')
    const isValueMultiple = !!typeLevel
    const showCondition = dateDiff <= 14 && !isValueMultiple

    return {
      aspectRatio: 5,
      responsive: true,
      legend: {
        display: false,
      },
      layout: {
        padding: {
          right: doubleChartsOn ? 70 : 10,
        },
      },
      tooltips: {
        enabled: !showCondition,
        backgroundColor: COLOR.DB_MINT,
        displayColors: false,
        callbacks: {
          title: () => '',
          label: ({ index }) => this.getDataByIndex(index),
        },
      },
      scales: {
        xAxes: [
          {
            offset: showCondition,
            offsetWidth: 600,
            type: 'time',
            time: {
              unit: 'day',
              displayFormats: {
                day: 'DD MM YYYY',
              },
            },
            ticks: {
              min: range.get('startDate').startOf('day'),
              max: range.get('endDate').startOf('day'),
              beginAtZero: true,
              minRotation: showCondition ? 0 : 0.001,
            },
          },
        ],
        yAxes: [
          {
            display: true,
            ticks: {
              beginAtZero: true,
              min: 0,
              max: 10,
              callback: () => null,
            },
            scaleLabel: {
              display: true,
              labelString: label || '',
            },
            afterFit: scale => {
              // eslint-disable-next-line no-param-reassign
              scale.width = 85
            },
          },
        ],
      },
      plugins: {
        datalabels: {
          display: showCondition,
          align: 'start',
          color: COLOR.MAIN_TEXT,
          labels: {
            value: {
              font: {
                size: 12,
              },
            },
          },
          formatter: (valueObj, pointObj) => {
            const index = pointObj.dataIndex

            return this.getDataByIndex(index)
          },
        },
      },
    }
  }

  onChronologyControl = method => {
    const { value } = method.currentTarget
    const { range, setActiveDateRange } = this.props
    const from = range.get('startDate').format('YYYY-MM-DD')
    const to = range.get('endDate').format('YYYY-MM-DD')
    const start = moment(from)
    const end = moment(to)
    const diff = end.diff(start, 'days')
    const countDays = diff > 30 ? 30 : diff

    if (value === 'prev') {
      setActiveDateRange({
        startDate: range.get('startDate').subtract(countDays, 'd'),
        endDate: range.get('endDate').subtract(countDays, 'd'),
      })
    } else {
      setActiveDateRange({
        startDate: range.get('startDate').add(countDays, 'd'),
        endDate: range.get('endDate').add(countDays, 'd'),
      })
    }

    this.getChartsData()
  }

  getChartsData() {
    const {
      range,
      entity,
      activeSite,
      logbookValue,
      productionValue,
      environmentValue,
      fetchLogbookData,
      fetchEnvironmentData,
      fetchProductionData,
    } = this.props

    const checkEntity = Object.keys(entity)
    const from = range.get('startDate').format('YYYY-MM-DD')
    const to = range.get('endDate').format('YYYY-MM-DD')
    const data = {
      ...entity,
      from,
      to,
    }

    if (checkEntity.length > 0 && logbookValue) {
      fetchLogbookData({
        event: logbookValue,
        ...data,
      })
    }

    if (checkEntity.length > 0 && productionValue) {
      fetchProductionData({
        type: productionValue,
        params: {
          ...data,
        },
      })
    }

    if (environmentValue && activeSite) {
      fetchEnvironmentData({
        sensor: environmentValue,
        site: activeSite,
        from,
        to,
      })
    }
  }

  render() {
    const { range, loader, doubleChartsOn } = this.props
    const yesterday = moment().subtract(1, 'd')
    const disableNextBtn = range.get('endDate').isSameOrAfter(yesterday)

    return (
      <div
        className={cn('control-chart', { 'doubled-charts': doubleChartsOn })}>
        <CircleLoader loading={loader} />
        <Line
          width={null}
          height={30}
          data={this.getData()}
          options={this.getOptions()}
        />
        <IconButton
          disableRipple
          onClick={this.onChronologyControl}
          color="primary"
          size="small"
          value="prev"
          className="navigate-btn date-prev">
          <ChevronLeft />
        </IconButton>
        <IconButton
          disableRipple
          disabled={disableNextBtn}
          onClick={this.onChronologyControl}
          color="primary"
          size="small"
          value="next"
          className="navigate-btn date-next">
          <ChevronRight />
        </IconButton>
      </div>
    )
  }
}

ControlChart.defaultProps = {
  data: {
    type: '',
    items: [],
  },
  label: '',
}

export default connect(
  state => ({
    dateRange: state.getIn(['sensor', 'dateRange']),
  }),
  {
    setActiveDateRange,
  },
)(injectIntl(ControlChart))
